import { html, unsafeCSS } from 'lit';
import { state, customElement, property } from 'lit/decorators.js';
import { ApiService } from '@trp-dms-digital-shared/ds-ui-library-api';

import { Constants as BaseConstants } from '../../base/base.constants.js';
import { PopApiBaseComponent } from '../../base/pop-api-base-component.js';
import type { Literature, LiteratureTable } from './literature-table.model.js';
import { Constants } from './literature-table.constants.js';
import { faiLogin, faiLoginStatus } from '../../common/utils/faiUserService.js';
import { FAILoginRegistrationCustomEvents } from '../../common/constants/index.js';
import style from './literature-table.scss?inline';
import { SummaryFragments } from '../../common/fragments/summary.fragments.js';

/**
 * @tag pop-literature-table-component
 * @property {LiteratureTable} [componentObject] Literature table content
 * @attribute {LiteratureTable} [data-component-object] Literature table content
 */
@customElement(Constants.TAG_NAME)
export class PopLiteratureTableComponent extends PopApiBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  /**
   * @internal
   */
  static readonly tagName = Constants.TAG_NAME;

  /**
   * @internal
   */
  componentName = PopLiteratureTableComponent.tagName;

  /**
   * @internal
   */
  @state()
  isUserLoggedIn = false;

  /**
   * The component object that contains the pre-fetched data to be rendered.
   */
  @property({
    attribute: BaseConstants.DATA_COMPONENT_OBJECT,
    type: Object,
  })
  componentObject?: LiteratureTable;

  constructor() {
    super(new ApiService());
  }

  updateLoginState() {
    this.isUserLoggedIn = faiLoginStatus();
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      FAILoginRegistrationCustomEvents.LOGGED_IN,
      this.updateLoginState.bind(this),
    );

    // This is a workaround for current event from the new login modal being dispatched on the document.body
    document.body.addEventListener(
      FAILoginRegistrationCustomEvents.LOGGED_IN,
      this.updateLoginState.bind(this),
    );

    document.addEventListener(
      FAILoginRegistrationCustomEvents.LOGGED_OUT,
      this.updateLoginState.bind(this),
    );

    // This is a workaround for current event from the new login modal being dispatched on the document.body
    document.body.addEventListener(
      FAILoginRegistrationCustomEvents.LOGGED_OUT,
      this.updateLoginState.bind(this),
    );

    this.updateComplete.then(() => {
      this.isUserLoggedIn = faiLoginStatus();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      FAILoginRegistrationCustomEvents.LOGGED_IN,
      this.updateLoginState.bind(this),
    );

    document.body.removeEventListener(
      FAILoginRegistrationCustomEvents.LOGGED_IN,
      this.updateLoginState.bind(this),
    );

    document.removeEventListener(
      FAILoginRegistrationCustomEvents.LOGGED_OUT,
      this.updateLoginState.bind(this),
    );

    document.body.removeEventListener(
      FAILoginRegistrationCustomEvents.LOGGED_OUT,
      this.updateLoginState.bind(this),
    );
  }

  /**
   * @internal
   */
  fragment = SummaryFragments.summaryFragment;

  protected processResponse(_response): void {
    this.componentObject = {
      productName: _response?.summaryExhibit?.fundName,
      isLiteratureSubscribable:
        _response?.summaryExhibit?.isLiteratureSubscribable,
      literatures: _response?.summaryExhibit?.literatures,
    };
  }

  /**
   * Certain literature is displayed as locked, but only to unauthenticated users.
   *
   * @param literature The literature item
   * @returns true when the literature should be displayed as locked, false otherwise
   */
  private shouldDisplayAsLocked(literature: Literature): boolean {
    return literature.locked && !this.isUserLoggedIn;
  }

  /**
   * Renders content to inform the user if the literature requires a login to view.
   *
   * Renders a lock icon visually and a hidden message for screen readers.
   */
  renderLockedLiteratureIndicator(literature: Literature) {
    return this.shouldDisplayAsLocked(literature)
      ? html`
          <span class="visually-hidden">${Constants.LOCKED_MESSAGE}</span>
          <span aria-hidden="true" class="literature-lock-icon">
            <beacon-icon slot="suffix" name="locked"></beacon-icon>
          </span>
        `
      : '';
  }

  renderDownloadLiteratureIcon() {
    return html`<beacon-icon slot="icon" name="download"></beacon-icon>`;
  }

  renderEmailIcon() {
    return html`<beacon-icon slot="icon" name="email"></beacon-icon>`;
  }

  renderSubscribeButton() {
    return this.productCode && this.componentObject?.isLiteratureSubscribable
      ? html`
          <div class="b-grid-item--2-col">
            <slot name="subscription-button"></slot>
          </div>
        `
      : html``;
  }

  handleDownloadIconClick(event: Event, literature: Literature) {
    if (literature.locked && !this.isUserLoggedIn) {
      event.preventDefault();
      faiLogin();
    } else {
      window.open(literature.url, Constants.BLANK);
    }
  }

  handleEmailIconClick(event: Event, literature: Literature) {
    if (literature.locked && !this.isUserLoggedIn) {
      event.preventDefault();
      faiLogin();
    }
  }

  private renderTableHead() {
    return html`
      <tr>
        <th id="literature" scope="col">
          <beacon-table-header variant="jumbo"
            >${Constants.LITERATURE}</beacon-table-header
          >
        </th>
        <th id="download" scope="col">
          <beacon-table-header variant="jumbo"
            >${Constants.DOWNLOAD}</beacon-table-header
          >
        </th>
        <th id="email" scope="col">
          <beacon-table-header variant="jumbo"
            >${Constants.EMAIL}</beacon-table-header
          >
        </th>
      </tr>
    `;
  }

  private createMailtoLink(literature: Literature): string {
    const subject = encodeURIComponent(Constants.EMAIL_SUBJECT_TEXT);

    const description =
      `${Constants.BEGINNING_EMAIL_BODY_TEXT}` +
      `${this.componentObject?.productName} ${literature.name} ` +
      `${Constants.ENDING_EMAIL_BODY_TEXT}`;

    // if the literature is not locked, the url should be the one specified.  If literature
    // is locked, the url should be the current page anchored at #literature.
    const currentPageURL = window.location.href;
    const currentPageURLWithoutHash = currentPageURL.split('#')[0];
    const URLHash = window.location.hash;
    let url = literature.url;
    if (literature.locked) {
      if (URLHash && URLHash === Constants.HASH_LITERATURE) {
        url = currentPageURL;
      } else {
        url = `${currentPageURLWithoutHash}${Constants.HASH_LITERATURE}`;
      }
    }

    const body = encodeURIComponent(`${description}\n\n${url}`);

    return `mailto:?subject=${subject}&body=${body}`;
  }

  private renderTableBody() {
    const literatures = this.componentObject?.literatures;

    if (!literatures) {
      return html``;
    }

    return literatures.map((literature) => {
      const mailtoLink = this.createMailtoLink(literature);
      const downloadIconButtonLabel = `Download ${literature.name}`;
      const emailIconButtonLabel = `Email ${literature.name}`;

      return html`
        <tr
          data-literature-name="${literature.name}"
          ?data-literature-locked="${this.shouldDisplayAsLocked(literature)}"
        >
          <th scope="row" class="literature-row-header">
            <beacon-table-stub>
              <span
                class="b-typography-sans-serif--body--100 b-font-weight-bold"
              >
                ${literature.name}
                ${this.renderLockedLiteratureIndicator(literature)}
              </span>
            </beacon-table-stub>
          </th>
          <td>
            <beacon-icon-button
              class="literature-download-icon-button"
              variant="tertiary"
              @beacon-event-click=${(event: Event) =>
                this.handleDownloadIconClick(event, literature)}
            >
              ${downloadIconButtonLabel} ${this.renderDownloadLiteratureIcon()}
            </beacon-icon-button>
          </td>
          <td>
            <beacon-icon-button
              class="literature-email-icon-button"
              variant="tertiary"
              link-href="${mailtoLink}"
              link-target="${Constants.BLANK}"
              @beacon-event-click=${(event: Event) =>
                this.handleEmailIconClick(event, literature)}
            >
              ${emailIconButtonLabel} ${this.renderEmailIcon()}
            </beacon-icon-button>
          </td>
        </tr>
      `;
    });
  }

  render() {
    if (!this.componentObject) {
      return html``;
    }
    return this.renderComponent(
      !this.componentObject?.literatures ||
        this.componentObject?.literatures?.length === 0
        ? html` <section class="literature-content b-container">
            <pop-no-data-available-component
              .message="${Constants.LITERATURE_UNAVAILABLE}"
            ></pop-no-data-available-component>
          </section>`
        : html` <section class="literature-content b-container">
            <div class="subscribe-button-container">
              ${this.renderSubscribeButton()}
            </div>
            <beacon-table id="literature-table">
              <table>
                <thead>
                  ${this.renderTableHead()}
                </thead>
                <tbody>
                  ${this.renderTableBody()}
                </tbody>
              </table>
            </beacon-table>
          </section>`,
    );
  }
}
